import { useEffect, useRef, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ExpandableRow } from "@/components/ui/responsive-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { ClaimDetailModal, type ClaimDetail } from "@/components/admin/claim-detail-modal";
import { useAdminContext } from "@/lib/admin-context";
import { showToast } from "@/lib/ui/toast";

/**
 * Admin → Claims (provider/facility/business claim review queue).
 *
 * Port of src/app/admin/claims/{page,claim-detail-modal}.tsx (legacy NextJS,
 * ~480 + ~1200 LOC) per D21 NO REINVENTION.
 *
 * Optimizations declared:
 *   - raw fetch + useAdminFetch hook → apiFetch (Architecture Discipline Rule 1)
 *   - useTranslations next-intl → Lingui Trans/t macros (D4)
 *   - useSearchParams + useRouter + usePathname (next/navigation) → TanStack
 *     Router validateSearch + useNavigate
 *   - useState/useEffect for fetch → useQuery (shared cache, auto-refetch)
 *   - showToast → imperative toast helper (lib/ui/toast.ts)
 *
 * Endpoints consumed (apps/api):
 *   GET    /api/admin/claims (list + filtering by status)
 *   PATCH  /api/admin/claims (approve/reject — wired via ClaimDetailModal)
 *   DELETE /api/admin/claims?id=<claimId> (delete — via modal or row action)
 *   GET    /api/admin/claims/:claimId/document/:slot/file (encrypted blob streamer)
 *   GET    /api/admin/anaf-lookup?cui=<cui> (ANAF cross-check — via modal)
 */

interface ClaimRequestRow {
  id: string;
  entityType: string;
  providerId: string | null;
  facilityId: string | null;
  businessId: string | null;
  userId: string;
  claimantName: string | null;
  claimantEmail: string | null;
  claimantPhone: string | null;
  status: string;
  verificationMethod: string | null;
  verificationData: Record<string, unknown> | null;
  licenseNo: string | null;
  taxId: string | null;
  adminNotes: string | null;
  reviewedAt: string | null;
  createdAt: string;
  entityName: string;
  entityCountry: string;
  entityDetails: Record<string, unknown> | null;
  claimant: {
    email: string | null;
    emailVerified: boolean;
    phone: string | null;
    phoneVerified: boolean;
  } | null;
}

const statusStyles: Record<string, string> = {
  pending: "border-amber-300 bg-amber-50 text-amber-700",
  approved: "border-emerald-300 bg-emerald-50 text-emerald-700",
  rejected: "border-red-300 bg-red-50 text-red-700",
};

type StatusFilter = "pending" | "approved" | "rejected" | "all";

interface ClaimsSearch {
  open?: string;
}

export const Route = createFileRoute("/_authenticated/claims")({
  validateSearch: (search: Record<string, unknown>): ClaimsSearch => ({
    open: typeof search.open === "string" ? search.open : undefined,
  }),
  component: ClaimsPage,
});

function ClaimsPage() {
  const { t } = useLingui();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { dbTarget } = useAdminContext();

  // Default the filter to `all` when deep-linked so we don't hide an
  // already-approved/rejected row from the opened modal.
  const initialFilter: StatusFilter = search.open ? "all" : "pending";
  const [filter, setFilter] = useState<StatusFilter>(initialFilter);
  const [processing, setProcessing] = useState<string | null>(null);
  const [modalClaim, setModalClaim] = useState<ClaimDetail | null>(null);

  // Track which ?open id has already been auto-popped. Without this, closing
  // the modal would fire the auto-open effect again because modalClaim flips
  // back to null and ?open is still in the URL.
  const autoOpenedIds = useRef<Set<string>>(new Set());

  const claimsQuery = useQuery({
    queryKey: ["admin", "claims", "list", filter, dbTarget],
    queryFn: async (): Promise<{ claims: ClaimRequestRow[]; total: number }> => {
      try {
        const params = new URLSearchParams({ limit: "100" });
        if (filter !== "all") params.set("status", filter);
        const data = await apiFetch.get<{ claims: ClaimRequestRow[]; total: number }>(
          `/api/admin/claims?${params.toString()}`,
        );
        return { claims: data.claims ?? [], total: data.total ?? 0 };
      } catch (error) {
        if (error instanceof ApiError) return { claims: [], total: 0 };
        throw error;
      }
    },
    staleTime: 30_000,
  });

  const claims = claimsQuery.data?.claims ?? [];
  const total = claimsQuery.data?.total ?? 0;

  // Deep-link: after claims load, auto-open the modal for the requested id.
  useEffect(() => {
    const openId = search.open;
    if (!openId || claims.length === 0) return;
    if (autoOpenedIds.current.has(openId)) return;
    const target = claims.find((c) => c.id === openId);
    if (target) {
      autoOpenedIds.current.add(openId);
      setModalClaim(target as ClaimDetail);
    }
  }, [search.open, claims]);

  const closeModal = () => {
    setModalClaim(null);
    if (search.open) {
      void navigate({
        search: (prev) => ({ ...prev, open: undefined }),
        replace: true,
      });
    }
  };

  const handleAction = async (claimId: string, action: "approve" | "reject") => {
    setProcessing(claimId);
    try {
      await apiFetch.patch("/api/admin/claims", { claimId, action });
      void claimsQuery.refetch();
    } catch (error) {
      const msg = error instanceof ApiError ? `${error.status}` : "unknown";
      showToast(t`Action failed (${msg})`, "error");
    } finally {
      setProcessing(null);
    }
  };

  const pendingCount = claims.filter((c) => c.status === "pending").length;
  const approvedCount = claims.filter((c) => c.status === "approved").length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-foreground text-2xl font-bold tracking-tight">
          <Trans>Claim requests</Trans>
        </h1>
        <p className="text-muted-foreground text-sm">
          <Trans>Review and approve provider, facility & business claim requests.</Trans>
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        {[
          {
            label: <Trans>Pending</Trans>,
            value: pendingCount,
            color: "text-amber-600",
          },
          {
            label: <Trans>Approved</Trans>,
            value: approvedCount,
            color: "text-emerald-600",
          },
          { label: <Trans>Total</Trans>, value: total, color: "text-foreground" },
        ].map((s, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <p className="text-muted-foreground text-xs font-medium uppercase">{s.label}</p>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex gap-2">
        {(["pending", "approved", "rejected", "all"] as const).map((f) => (
          <Button
            key={f}
            size="sm"
            variant={filter === f ? "default" : "outline"}
            onClick={() => setFilter(f)}
          >
            {f === "all" ? (
              <Trans>All</Trans>
            ) : f === "pending" ? (
              <Trans>Pending</Trans>
            ) : f === "approved" ? (
              <Trans>Approved</Trans>
            ) : (
              <Trans>Rejected</Trans>
            )}
          </Button>
        ))}
      </div>

      <Card>
        <CardContent className="p-0">
          {claimsQuery.isLoading ? (
            <div className="text-muted-foreground p-8 text-center text-sm">
              <Trans>Loading…</Trans>
            </div>
          ) : claims.length === 0 ? (
            <div className="text-muted-foreground p-8 text-center text-sm">
              {filter === "all" ? (
                <Trans>No claims found.</Trans>
              ) : filter === "pending" ? (
                <Trans>No pending claims found.</Trans>
              ) : filter === "approved" ? (
                <Trans>No approved claims found.</Trans>
              ) : (
                <Trans>No rejected claims found.</Trans>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="hidden sm:table-cell">
                    <Trans>Type</Trans>
                  </TableHead>
                  <TableHead>
                    <Trans>Entity</Trans>
                  </TableHead>
                  <TableHead className="hidden md:table-cell">
                    <Trans>Claimant</Trans>
                  </TableHead>
                  <TableHead className="hidden sm:table-cell">
                    <Trans>Method</Trans>
                  </TableHead>
                  <TableHead className="hidden md:table-cell">
                    <Trans>License / Tax ID</Trans>
                  </TableHead>
                  <TableHead>
                    <Trans>Status</Trans>
                  </TableHead>
                  <TableHead className="hidden lg:table-cell">
                    <Trans>Date</Trans>
                  </TableHead>
                  <TableHead className="text-right">
                    <Trans>Actions</Trans>
                  </TableHead>
                  <TableHead className="w-8 md:hidden" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {claims.map((claim) => (
                  <ExpandableRow
                    key={claim.id}
                    mobileDetails={[
                      {
                        label: <Trans>Type</Trans>,
                        value: (
                          <Badge variant="outline" className="text-xs">
                            {claim.entityType === "provider" ? (
                              <Trans>Provider</Trans>
                            ) : claim.entityType === "business" ? (
                              <Trans>Business</Trans>
                            ) : (
                              <Trans>Facility</Trans>
                            )}
                          </Badge>
                        ),
                      },
                      {
                        label: <Trans>Method</Trans>,
                        value: claim.verificationMethod || "—",
                      },
                      {
                        label: <Trans>Claimant</Trans>,
                        value: (
                          <div className="space-y-1">
                            <p className="text-sm">{claim.claimantName || "—"}</p>
                            {claim.claimant?.email && (
                              <p className="text-xs">
                                <span className="text-muted-foreground">email:</span>{" "}
                                {claim.claimant.email}
                              </p>
                            )}
                          </div>
                        ),
                      },
                      {
                        label: <Trans>Date</Trans>,
                        value: new Date(claim.createdAt).toLocaleDateString(),
                      },
                    ]}
                  >
                    <TableCell className="hidden sm:table-cell">
                      <Badge variant="outline" className="text-xs">
                        {claim.entityType === "provider" ? (
                          <Trans>Provider</Trans>
                        ) : claim.entityType === "business" ? (
                          <Trans>Business</Trans>
                        ) : (
                          <Trans>Facility</Trans>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-sm font-medium">{claim.entityName || "—"}</p>
                        <p className="text-muted-foreground text-xs">{claim.entityCountry}</p>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div>
                        <p className="text-sm">{claim.claimantName || "—"}</p>
                        <p className="text-muted-foreground text-xs">
                          {claim.claimantEmail || "—"}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <span className="text-xs">{claim.verificationMethod || "—"}</span>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <span className="font-mono text-xs">
                        {claim.licenseNo || claim.taxId || "—"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={statusStyles[claim.status] || ""}>
                        {claim.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <span className="text-muted-foreground text-xs">
                        {new Date(claim.createdAt).toLocaleDateString()}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setModalClaim(claim as ClaimDetail);
                          }}
                        >
                          <Trans>Details</Trans>
                        </Button>
                        {claim.status === "pending" && (
                          <Button
                            size="sm"
                            variant="default"
                            disabled={processing === claim.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleAction(claim.id, "approve");
                            }}
                          >
                            <Trans>Approve</Trans>
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </ExpandableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <ClaimDetailModal
        claim={modalClaim}
        open={!!modalClaim}
        onClose={closeModal}
        onActionComplete={() => {
          void claimsQuery.refetch();
        }}
      />
    </div>
  );
}
